import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { X, Send, Sparkles } from "lucide-react";

const GIACOMO_IMG = "https://customer-assets-gfyr7b9c.emergentagent.net/job_culto-vote/artifacts/usqsv43m_GIACOMO%20-%203.PNG";
const SUGGESTIONS = ["Quando è la prossima riunione?", "Ci sono votazioni aperte?", "Come attivo le notifiche?", "Come scarico la mia tessera?"];

function sessionId() {
  let s = localStorage.getItem("giacomo-session");
  if (!s) { s = `s_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem("giacomo-session", s); }
  return s;
}

export default function GiacomoChat({ open, onClose, userName }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const sid = useRef(sessionId());

  useEffect(() => {
    if (!open) return;
    api.get(`/assistant/history?session_id=${sid.current}`).then(r => setMsgs((r.data || []).map((m, idx) => ({ ...m, _k: m.id || `h-${idx}` })))).catch(() => {});
  }, [open]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || busy) return;
    setInput("");
    setMsgs(m => [...m, { role: "user", content: t, _k: `u-${Date.now()}` }, { role: "assistant", content: "", _k: `a-${Date.now()}` }]);
    setBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assistant/chat`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid.current, message: t }),
      });
      if (!res.ok || !res.body) throw new Error("http " + res.status);
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { value, done } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data: /, "");
          if (line === "[DONE]") continue;
          try { const { t: tok } = JSON.parse(line); setMsgs(m => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + tok }; return c; }); } catch (e) { /* ignore */ }
        }
      }
    } catch (e) {
      setMsgs(m => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], role: "assistant", content: "Ops, non riesco a rispondere adesso. Riprova tra poco!" }; return c; });
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div data-testid="giacomo-chat" className="fixed z-[70] inset-x-0 bottom-0 sm:inset-auto sm:right-5 sm:bottom-5 sm:w-[380px] sm:max-h-[600px] h-[80vh] sm:h-[560px] bg-white sm:rounded-2xl rounded-t-2xl shadow-2xl border border-[#E8E2D8] flex flex-col overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E8E2D8] bg-[#FDF2F2]">
        <img src={GIACOMO_IMG} alt="Giacomo" className="w-10 h-10 object-contain" />
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-[#2B3A33] leading-tight">Giacomo</div>
          <div className="text-[11px] text-[#6B6558] flex items-center gap-1"><Sparkles className="w-3 h-3 text-[#EF3E36]" /> Assistente virtuale Amunì</div>
        </div>
        <button onClick={onClose} data-testid="giacomo-chat-close" className="p-1.5 rounded-lg hover:bg-white text-[#6B6558]"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 text-sm">
        {msgs.length === 0 && (
          <div className="space-y-3">
            <div className="bg-[#F3EEE4] rounded-2xl rounded-tl-sm px-3 py-2 text-[#2B3A33]">Ciao {userName?.split(" ")[0] || ""}! Sono Giacomo 🤙 Chiedimi come funziona l'app, i prossimi eventi o le votazioni aperte.</div>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map(s => <button key={s} onClick={() => send(s)} data-testid="giacomo-suggestion" className="text-xs px-3 py-1.5 rounded-full border border-[#E8E2D8] bg-white hover:bg-[#FDF2F2] text-[#2B3A33]">{s}</button>)}
            </div>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={m._k ?? i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div data-testid={`giacomo-msg-${m.role}`} className={`max-w-[85%] whitespace-pre-wrap px-3 py-2 rounded-2xl ${m.role === "user" ? "bg-[#EF3E36] text-white rounded-tr-sm" : "bg-[#F3EEE4] text-[#2B3A33] rounded-tl-sm"}`}>
              {m.content || <span className="inline-block w-8 text-[#8A8272] animate-pulse">…</span>}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form onSubmit={e => { e.preventDefault(); send(); }} className="p-3 border-t border-[#E8E2D8] flex gap-2">
        <input data-testid="giacomo-input" value={input} onChange={e => setInput(e.target.value)} placeholder="Scrivi a Giacomo…" className="flex-1 min-w-0 px-3 py-2 rounded-xl border border-[#E8E2D8] text-sm" />
        <button data-testid="giacomo-send" disabled={busy || !input.trim()} className="px-3 py-2 rounded-xl bg-[#EF3E36] text-white disabled:opacity-50"><Send className="w-4 h-4" /></button>
      </form>
    </div>
  );
}
